import { AccountCircleIcon, ExploreIcon, ForumIcon, GroupIcon, HomeIcon, MedicalServicesIcon } from '@medly-components/icons';
import { useCallback, useContext, useMemo } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import {
    ACCOUNT_SETTINGS_ROUTE,
    APPOINTMENTS_ROUTE,
    GEAR_ROUTE,
    GUIDES_ROUTE,
    HOME_ROUTE,
    MAP_INFO_ROUTE,
    MAP_ROUTE,
    MEDICATIONS_ROUTE,
    MY_MAP_ROUTE,
    MY_PROFILE,
    SYMPTOMS_ROUTE,
    USER_DETAILS,
    USER_MANAGEMENT_ROUTE
} from 'constants/routes';

import { MapTourContext } from 'context/MapTourContext';
import { TourType } from 'pages/Map/components/MapTour';
import { useLogoutFlow } from '../../../hooks/useLogout/useLogoutFlow';
import { NavLink } from './types';

export const useSideNavLinks = (showLegends: boolean, admin: boolean, toggleLegends: () => void): [NavLink[], boolean] => {
    const { pathname } = useLocation();
    const { logout, loading } = useLogoutFlow();
    const { isCurrentTourItem } = useContext(MapTourContext);
    const isMapRoute = useMatch(MAP_ROUTE);

    const checkActive = useCallback(
        (path: string) => {
            return pathname === path && !showLegends;
        },
        [pathname, showLegends]
    );

    const isMapTourLegenedsActive = useMemo(() => {
        return isCurrentTourItem(TourType.INFO_ABOUT_LEGENEDS);
    }, [isCurrentTourItem]);

    const links = useMemo(
        (): NavLink[] => [
            {
                title: 'Home',
                path: HOME_ROUTE,
                isActive: checkActive(HOME_ROUTE),
                icon: <HomeIcon />
            },
            {
                title: 'Map',
                path: '',
                icon: <ExploreIcon />,
                isActive: isMapTourLegenedsActive,
                subNavLinks: [
                    {
                        title: 'My Map',
                        path: MY_MAP_ROUTE,
                        isActive: [MY_MAP_ROUTE].some(checkActive) || !!isMapRoute
                    },
                    {
                        title: 'Legends',
                        isActive: showLegends,
                        onClick: toggleLegends,
                        externalExpand: true
                    },
                    {
                        title: 'Map Info',
                        path: MAP_INFO_ROUTE,
                        isActive: checkActive(MAP_INFO_ROUTE)
                    }
                ]
            },
            {
                title: 'Gear',
                path: GEAR_ROUTE,
                isActive:
                    (pathname === GEAR_ROUTE ||
                        pathname.startsWith(APPOINTMENTS_ROUTE) ||
                        pathname.startsWith(SYMPTOMS_ROUTE) ||
                        pathname.startsWith(MEDICATIONS_ROUTE)) &&
                    !showLegends,
                icon: <MedicalServicesIcon />
            },
            {
                title: 'Guide',
                path: GUIDES_ROUTE,
                isActive: pathname.startsWith(GUIDES_ROUTE),
                icon: <ForumIcon />
            },
            {
                title: 'User Management',
                path: USER_MANAGEMENT_ROUTE,
                isActive: pathname === USER_MANAGEMENT_ROUTE || pathname.startsWith(USER_DETAILS),
                icon: <GroupIcon />
            },
            {
                title: 'My Account',
                path: '',
                icon: <AccountCircleIcon />,
                subNavLinks: [
                    {
                        title: 'My profile',
                        path: MY_PROFILE,
                        isActive: checkActive(MY_PROFILE)
                    },
                    {
                        title: 'Settings',
                        path: ACCOUNT_SETTINGS_ROUTE,
                        isActive: checkActive(ACCOUNT_SETTINGS_ROUTE)
                    },
                    {
                        title: 'Logout',
                        onClick: () => logout(false)
                    }
                ]
            }
        ],
        [checkActive, logout, showLegends, toggleLegends, isMapTourLegenedsActive, pathname]
    );
    const Navlinks = admin ? links : links.filter(link => link.path !== USER_MANAGEMENT_ROUTE);

    return [Navlinks, loading];
};
